import React from 'react';
import { Helmet } from 'react-helmet';

const SEO = ({
    title = 'Mahi Beach Ayurveda Resort — Authentic Beachfront Ayurveda in Maggona, Sri Lanka',
    description = 'Discover authentic Ayurveda wellness at Mahi Beach Ayurveda Resort: Beachfront resort in Maggona with oceanfront therapies, yoga, organic meals, and traditional Panchakarma treatments. Experience holistic healing with sea view accommodations in Sri Lanka. Book your Ayurveda retreat today.',
    keywords = 'Mahi Beach Ayurveda, Ayurveda resort Maggona, Ayurveda resort Sri Lanka, Panchakarma Sri Lanka, beach Ayurveda resort, authentic Ayurveda treatments, yoga retreat Sri Lanka, detox retreat Sri Lanka, wellness resort Maggona, ayurvedic holiday Sri Lanka, beachfront Ayurveda, sea view Ayurveda resort, Maggona wellness resort',
    image = 'https://mahibeachayurveda.com/images/mahi-beach-hero.jpg',
    url = 'https://mahibeachayurveda.com',
    pathname = '/',
}) => {
    const pageUrl = `${url}${pathname.startsWith('/') ? pathname : `/${pathname}`}`.replace(/\/+/g, '/');

    // Structured data for Mahi Beach Ayurveda Resort
    const structuredData = [
        {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Mahi Beach Ayurveda Resort",
            "url": "https://mahibeachayurveda.com",
            "logo": "https://mahibeachayurveda.com/logo.png",
            "sameAs": [
                "https://www.facebook.com/mahibeachayurveda",
                "https://www.instagram.com/mahibeachayurveda",
                "https://www.tripadvisor.com/mahibeachayurveda"
            ]
        },
        {
            "@context": "https://schema.org",
            "@type": "Hotel",
            "name": "Mahi Beach Ayurveda Resort",
            "url": "https://mahibeachayurveda.com",
            "description": "Authentic beachfront Ayurveda resort in Maggona offering traditional Panchakarma, Abhyanga massage, yoga, herbal treatments, organic Sri Lankan cuisine, and direct beach access with sea view rooms.",
            "image": "https://mahibeachayurveda.com/images/mahi-beach-hero.jpg",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "Beach Side",
                "addressLocality": "Maggona",
                "addressRegion": "Western Province",
                "postalCode": "12060",
                "addressCountry": "LK"
            },
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": 6.4789,
                "longitude": 79.9821
            },
            "telephone": "+94 77 738 2466",
            "priceRange": "$$-$$$",
            "starRating": {
                "@type": "Rating",
                "ratingValue": "4.5"
            },
            "amenityFeature": [
                { "@type": "LocationFeatureSpecification", "name": "Beachfront" },
                { "@type": "LocationFeatureSpecification", "name": "Sea View Rooms" },
                { "@type": "LocationFeatureSpecification", "name": "Yoga Pavilion" },
                { "@type": "LocationFeatureSpecification", "name": "Ayurvedic Restaurant" },
                { "@type": "LocationFeatureSpecification", "name": "Free WiFi" },
                { "@type": "LocationFeatureSpecification", "name": "Panchakarma Centre" },
                { "@type": "LocationFeatureSpecification", "name": "Beach Access" },
                { "@type": "LocationFeatureSpecification", "name": "Organic Meals" }
            ],
            "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Ayurveda Treatments & Accommodation",
                "itemListElement": [
                    {
                        "@type": "Offer",
                        "itemOffered": {
                            "@type": "Service",
                            "name": "Deluxe Room with Sea View",
                            "description": "Spacious deluxe room with direct sea view, modern amenities, and Ayurvedic comfort"
                        }
                    },
                    {
                        "@type": "Offer",
                        "itemOffered": {
                            "@type": "Service",
                            "name": "Panchakarma Treatment",
                            "description": "Traditional Ayurvedic detoxification and rejuvenation therapy"
                        }
                    },
                    {
                        "@type": "Offer",
                        "itemOffered": {
                            "@type": "Service",
                            "name": "Yoga & Meditation",
                            "description": "Daily yoga sessions and guided meditation by the beach"
                        }
                    }
                ]
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "url": "https://mahibeachayurveda.com",
            "name": "Mahi Beach Ayurveda Resort",
            "potentialAction": {
                "@type": "SearchAction",
                "target": "https://mahibeachayurveda.com/search?q={search_term_string}",
                "query-input": "required name=search_term_string"
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "TouristDestination",
            "name": "Mahi Beach Ayurveda Resort - Maggona Wellness Retreat",
            "description": "Premier Ayurveda wellness destination in Maggona, Sri Lanka, offering authentic beachfront healing experiences",
            "url": "https://mahibeachayurveda.com",
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": 6.4789,
                "longitude": 79.9821
            },
            "touristType": [
                "Wellness Seekers",
                "Yoga Enthusiasts",
                "Ayurveda Practitioners",
                "Beach Lovers",
                "Health Conscious Travelers"
            ]
        }
    ];

    return (
        <Helmet>
            <html lang="en" />
            <title>{title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={keywords} />
            <meta name="author" content="Mahi Beach Ayurveda Resort" />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta name="googlebot" content="index, follow" />

            {/* Canonical – very important */}
            <link rel="canonical" href={pageUrl} />

            {/* Open Graph – better for social & WhatsApp sharing */}
            <meta property="og:locale" content="en_US" />
            <meta property="og:site_name" content="Mahi Beach Ayurveda Resort" />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Mahi Beach Ayurveda Resort Maggona Sri Lanka - Beachfront Wellness Retreat" />
            <meta property="og:url" content={pageUrl} />
            <meta property="og:type" content="website" />

            {/* Twitter / X Cards */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:site" content="@mahibeachayur" />

            {/* Additional SEO Meta Tags */}
            <meta name="geo.region" content="LK-WP" />
            <meta name="geo.placename" content="Maggona" />
            <meta name="geo.position" content="6.4789;79.9821" />
            <meta name="ICBM" content="6.4789, 79.9821" />

            {/* JSON-LD – very powerful for rich results */}
            <script type="application/ld+json">
                {JSON.stringify(structuredData)}
            </script>
        </Helmet>
    );
};

export default SEO;